import { useEffect, useState } from 'react';
import { SelectItem } from '@/components/ui/select';
import { SelectField } from './SelectField';
import { useNode } from '@craftjs/core';
import { useFonts } from '@/context/FontProvider';
import { editorRegistry } from '@/components/userComponents/Text';
import { cn } from '@/lib/utils';
import { applyMark } from '@/utils/textUtils';
import { Editor as SlateEditor } from 'slate';
import type { ButtonProps } from '@builder4/shared/components/Button';
import { isTextBlockNode } from '@/utils/helperFunc';

const NodeFontWeightSettings = ({
  fontFamily,
  className = 'max-w-[216px]',
}: {
  fontFamily?: { family: string; variants: string[] };
  className?: string;
}) => {
  const { fontManager } = useFonts();

  const {
    id,
    props,
    actions: { setProp },
    node,
  } = useNode((node) => ({
    node: node,
    id: node.id,
    props: node.data.props,
  }));

  const [weights, setWeights] = useState<
    Array<{ label: string; value: string; weight: string; style: string }>
  >([]);

  useEffect(() => {
    if (fontFamily && fontManager) {
      const weights = fontManager.getAvailableWeights(fontFamily.family);
      // Filter out italic variants per requirement
      const nonItalicWeights = weights.filter((weight) => !/italic/i.test(weight.value));
      setWeights(nonItalicWeights);
    }
  }, [fontFamily, fontManager]);

  const handleWeightChange = (variant: string) => {
    const weight = weights.find((weight) => weight.value === variant);
    if (!weight) return;
    if (isTextBlockNode(node)) {
      const slateEditor = editorRegistry.get(id);
      if (!slateEditor) return;
      const { editor } = slateEditor;
      applyMark(editor, 'fontWeight', weight.weight);
      applyMark(editor, 'fontStyle', weight.style);
    } else {
      setProp((props: Partial<ButtonProps>) => {
        props.fontWeight = weight.weight;
        props.fontStyle = weight.style;
      }, 500);
    }
  };

  const getFontVariant = () => {
    if (isTextBlockNode(node)) {
      const slateEditor = editorRegistry.get(id);
      if (!slateEditor) return '';
      const { editor } = slateEditor;
      const marks = SlateEditor.marks(editor) as Record<string, string>;

      let varient = '';
      if (marks && Object.keys(marks)?.length > 0) {
        const fontFamily = marks['fontFamily'] || '';
        let weights = fontManager?.getAvailableWeights(fontFamily) || []; //sorted weights

        let fontWeight = marks['fontWeight'];
        if (!fontWeight || fontWeight === 'normal') {
          fontWeight = '400';
        }
        if (fontWeight === 'bold') {
          //get largest weight that is bold, as getAvailableWeights returns sorted weights so no need to sort again
          weights = weights.filter((weight) => parseInt(weight.weight) >= 400);
          if (weights.length > 0) {
            fontWeight = weights[weights.length - 1].weight;
          }
        }

        const weightObject = weights.find((v) => v.weight === fontWeight);
        varient = weightObject?.value || '';
      }
      return varient || 'regular';
    } else {
      let weights = fontManager?.getAvailableWeights(props.fontFamily) || []; //sorted weights
      let fontWeight = props.fontWeight;
      if (!fontWeight || fontWeight === 'normal') {
        fontWeight = '400';
      }
      if (fontWeight === 'bold') {
        //get largest weight that is bold, as getAvailableWeights returns sorted weights so no need to sort again
        weights = weights.filter((weight) => parseInt(weight.weight) >= 400);
        if (weights.length > 0) {
          fontWeight = weights[weights.length - 1].weight;
        }
      }

      const weightObject = weights.find((v) => v.weight === fontWeight);
      return weightObject?.value || 'regular';
    }
  };

  return (
    <SelectField
      onValueChange={handleWeightChange}
      className={cn(className)}
      defaultValue={getFontVariant()}
      size="small"
      text="Weight"
      truncate={false}
      dataTestId="weight-selection"
    >
      {weights.length > 0 ? (
        weights.map((weight) => (
          <SelectItem key={weight.value} value={weight.value}>
            {weight.label}
          </SelectItem>
        ))
      ) : (
        <SelectItem value="loading" disabled>
          Loading...
        </SelectItem>
      )}
    </SelectField>
  );
};

export default NodeFontWeightSettings;
